import { useEffect, useRef } from 'react';
import { useActions, useSettings } from '@/store/selectors';

/**
 * First-run disclaimer modal (PSD §8.4 / TODO Task 1.19, 3.16).
 * Blocks interaction until acknowledged; ack persists to localStorage.
 */
export function DisclaimerModal(): JSX.Element | null {
  const actions = useActions();
  const settings = useSettings();
  const ackBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!settings.disclaimerAcknowledged) {
      ackBtn.current?.focus();
    }
  }, [settings.disclaimerAcknowledged]);

  useEffect(() => {
    if (settings.disclaimerAcknowledged) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        actions.acknowledgeDisclaimer();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [settings.disclaimerAcknowledged, actions]);

  if (settings.disclaimerAcknowledged) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Disclaimer" onClick={() => actions.acknowledgeDisclaimer()}>
      <div className="modal modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Clinical Decision Support — Disclaimer</h2>
        <div className="modal-text disclaimer-body">
          <p>
            <strong>This tool is clinical decision support, not a prescriber.</strong> All
            dosing recommendations must be reviewed and approved by a qualified clinician
            before administration.
          </p>
          <ul>
            <li>
              Aminoglycosides have a narrow therapeutic index and carry risk of nephrotoxicity
              and ototoxicity.
            </li>
            <li>
              Outputs are estimates derived from population pharmacokinetics and may not reflect
              individual patient variability.
            </li>
            <li>
              Extended-interval contraindications and renal thresholds are screened, but clinical
              judgment supersedes any automated recommendation.
            </li>
            <li>
              All computation and stored data remain on this device (offline-first). No patient
              data is transmitted.
            </li>
          </ul>
        </div>
        <div className="modal-actions">
          <button
            className="btn"
            ref={ackBtn}
            onClick={() => actions.acknowledgeDisclaimer()}
            data-testid="disclaimer-ack"
          >
            I acknowledge the disclaimer
          </button>
        </div>
      </div>
    </div>
  );
}
